import { cn } from '@scio3/ui';
import { TEAMSPACE_ICONS } from './teamspace-icons';

const SIZE = {
  sm: { tile: 'size-5 rounded-sm text-xs', icon: 'size-3.5' },
  md: { tile: 'size-9 rounded-md text-base', icon: 'size-5' },
  lg: { tile: 'size-12 rounded-lg text-xl', icon: 'size-7' },
} as const;

const HEX_RE = /^#[0-9a-f]{6}$/i;
/** ~15% alpha (`26` hex) of the picked colour — the tile tint behind a coloured icon. */
const tint = (hex: string) => `${hex}26`;

/**
 * A teamspace's mark, always on a rounded tile (Jessica, 2026-10-05):
 *  - a picked icon (`team.icon`) in `team.iconColor`, on a tile tinted with that colour at low
 *    opacity (grey when no colour was picked);
 *  - otherwise the name's first letter on a plain grey tile. Letters are ALWAYS neutral — a colour
 *    saved before this change is ignored, and the picker no longer offers one for letters.
 * Both looks occupy the same box, so rows align whichever a teamspace uses.
 */
export function TeamspaceIcon({
  name,
  icon,
  color,
  size = 'md',
  className,
}: {
  /**
   * The teamspace's name. Declared NULLABLE on purpose: this was `string`, which type-checked while
   * callers fed it `teamName` typed `?: string` — and `?:` admits `undefined`, NOT the `null` GraphQL
   * actually returns. A teamspace with no name reached `name.trim()` and threw, and because this is
   * rendered inside the route tree the throw took the whole Group Security page into the error
   * boundary ("Something went wrong"). A missing name must degrade to a placeholder, never crash —
   * `.claude/rules/frontend/type-safety.md`: swallow and render.
   */
  name?: string | null;
  icon?: string | null;
  color?: string | null;
  size?: keyof typeof SIZE;
  className?: string;
}) {
  const Icon = icon ? TEAMSPACE_ICONS[icon] : undefined;
  const s = SIZE[size];
  if (Icon) {
    const hex = color && HEX_RE.test(color) ? color : undefined;
    return (
      <span
        className={cn(
          'flex shrink-0 items-center justify-center bg-muted text-muted-foreground',
          s.tile,
          className,
        )}
        // A picked swatch (validated #rrggbb here and on the API), so inline style is safe; the
        // tint needs the runtime colour, which no Tailwind class can express.
        style={hex ? { color: hex, backgroundColor: tint(hex) } : undefined}
        aria-hidden
        data-testid="teamspace-icon"
      >
        <Icon className={s.icon} strokeWidth={1.75} />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center bg-muted font-medium text-muted-foreground',
        s.tile,
        className,
      )}
      aria-hidden
      data-testid="teamspace-icon"
    >
      {(name ?? '').trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
}
